"use client";

import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";

const MIN_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const PERIODIC_HEARTBEAT_INTERVAL_MS = 15 * 60 * 1000;

export default function ActivityHeartbeat() {
  const lastHeartbeatAtRef = useRef(0);

  useEffect(() => {
    let isMounted = true;

    async function sendHeartbeat() {
      const now = Date.now();
      if (now - lastHeartbeatAtRef.current < MIN_HEARTBEAT_INTERVAL_MS) {
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!isMounted || !user) {
        return;
      }

      lastHeartbeatAtRef.current = now;
      const timestamp = new Date(now).toISOString();

      const { error } = await supabase.from("user_preferences").upsert({
        user_id: user.id,
        last_active_at: timestamp,
        last_inactive_notification_sent_at: null,
        updated_at: timestamp,
      });

      if (
        error &&
        !error.message.includes("last_active_at") &&
        !error.message.includes("last_inactive_notification_sent_at")
      ) {
        console.error("Activity heartbeat failed:", error.message);
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void sendHeartbeat();
      }
    }

    function handleFocus() {
      void sendHeartbeat();
    }

    void sendHeartbeat();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void sendHeartbeat();
      }
    }, PERIODIC_HEARTBEAT_INTERVAL_MS);

    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
}
